import { articlePath, contextualArticle } from '../../help/catalogue';
import { resolveHelpBrand } from '../../help/branding';
import { useEffect, useState } from "react";
import { Link as RouterLink, useLocation } from "react-router-dom-v5-compat";
import PropTypes from "prop-types";
import {
  Avatar,
  Box,
  Divider,
  Drawer,
  Hidden,
  Link,
  IconButton,
  Typography,
  Button,
  Tooltip,
} from "@mui/material";
import {
  Timer,
  SupervisorAccount,
  ListAlt,
  List,
  Notifications,
  LocalOffer,
  CameraAlt,
  Share,
  AccountTree,
  DataUsage,
  CarRental,
  SettingsInputComponent,
  MenuBook,
  DeviceHub,
  LocalShipping,
  Grading,
  AirplaneTicket,
  CardMembership,
  GridOn,
  CalendarMonth,
  Crop75,
  Build,
  History,
  HelpOutline,
  OpenInNew,
  UploadFile,
} from "@mui/icons-material";

import DirectionsCar from "@mui/icons-material/DirectionsCar";
import CategoryIcon from "@mui/icons-material/Category";
import AssignmentIcon from "@mui/icons-material/Assignment";
import FileCopyIcon from "@mui/icons-material/FileCopy";

import {
  Person,
  BadgeOutlined,
  SettingsInputAntenna,
  LocationOn,
  Group,
  Code,
  AccountBalance,
  Payments,
} from "@mui/icons-material";
import useAuth from "../../hooks/useAuth";
import useSettings from "../../hooks/useSettings";

import ChartSquareBarIcon from "../../icons/ChartSquareBar";

import UserIcon from "../../icons/User";
import Business from "@mui/icons-material/Business";

import People from "@mui/icons-material/People";
import ChevronLeftIcon from "src/icons/ChevronLeft";
import ChevronRightIcon from "src/icons/ChevronRight";
import BorderClearIcon from "@mui/icons-material/BorderClear";
import colors from "src/colors";
import AccountTreeIcon from "@mui/icons-material/AccountTree";

import NavSection from "../NavSection";
import Scrollbar from "../Scrollbar";
import { AcUnit, DriveEta, Warning } from "@mui/icons-material";
import { styled } from "@mui/material/styles";

import { LicenseInfo } from "@mui/x-data-grid-premium";

import { Search as SearchIcon, Clear as ClearIcon } from "@mui/icons-material";
import { InputAdornment, TextField } from "@mui/material";
import { isCompanyFeatureEnabled } from "../../utils/companyFeatures";
import {
  canAccessVehicleAssignment,
  isVehicleAssignmentPath,
} from "../../utils/vehicleAssignmentAccess";

LicenseInfo.setLicenseKey(
  "35225cb0f52bc2b6e9cf35f96c15534eTz0xMjkwOTcsRT0xODEwMDc5OTk5MDAwLFM9cHJlbWl1bSxMTT1hbm51YWwsUFY9UTEtMjAyNixRPTEsQVQ9bXVsdGksS1Y9Mg=="
);

const DRAWER_WIDTH = 280;
const AUTO_MINIMISE_URL = [/\/dashboard\/assets\/device[^ ]*/i];

const openedMixin = (theme) => ({
  width: DRAWER_WIDTH,
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.easeOut,
    duration: theme.transitions.duration.shorter,
  }),
  willChange: "width",
  overflowX: "hidden",
});

const closedMixin = (theme) => ({
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.easeOut,
    duration: theme.transitions.duration.shorter,
  }),
  willChange: "width",
  overflowX: "hidden",
  width: `calc(${theme.spacing(10)} + 1px)`,
  minWidth: "80px",
});

const ExpandableDrawer = styled(Drawer, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  width: DRAWER_WIDTH,
  flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing: "border-box",
  contain: "layout",
  ...(open && {
    ...openedMixin(theme),
    "& .MuiDrawer-paper": openedMixin(theme),
  }),
  ...(!open && {
    ...closedMixin(theme),
    "& .MuiDrawer-paper": closedMixin(theme),
  }),
}));

const sections = [
  {
    title: "Overview",
    items: [
      {
        title: "Dashboard",
        path: "/dashboard/fleet",
        icon: <ChartSquareBarIcon fontSize="small" />,
        feature: "web_dashboard",
      },
      {
        title: "Fleet & Asset Map",
        path: "/dashboard/assets",
        icon: <DriveEta fontSize="small" />,
        feature: "web_asset_map",
      },
      {
        title: "Notifications",
        path: "/dashboard/notifications",
        icon: <Notifications fontSize="small" />,
        feature: "web_notifications",
      },
      {
        title: "Dashcam Events",
        path: "/dashboard/dashcam-events",
        icon: <CameraAlt fontSize="small" />,
        feature: "dashcam",
      },
    ],
  },
  {
    title: "Reporting & Insights",
    items: [
      {
        title: "Reporting",
        path: "/dashboard/reports",
        icon: <ListAlt fontSize="small" />,
        feature: "reporting",
      },
      {
        title: "Cold Chain",
        path: "/dashboard/environment",
        icon: <AcUnit fontSize="small" />,
        feature: "cold_chain",
      },
      {
        title: "Coverage Analysis",
        path: "/coverage-map",
        icon: <Crop75 fontSize="small" />,
        feature: "web_coverage_analysis",
      },
      {
        title: "Automations",
        path: "/dashboard/automations",
        icon: <AccountTreeIcon fontSize="small" />,
        feature: "automation",
      },
      {
        title: "Insight Tags",
        path: "/dashboard/insight-tags",
        feature: "insight_tags",
        icon: <LocalOffer fontSize="small" />,
      },
    ],
  },

  {
    title: "Maintenance",
    items: [
      {
        title: "Maintenance Board",
        path: "/dashboard/maintenance-board",
        icon: <Build fontSize="small" />, // Kept as is - wrench/tool icon is appropriate
        feature: "vehicle_task_management",
      },
      {
        title: "Vehicle Details & Service",
        path: "/dashboard/vehicle-maintenance",
        icon: <DirectionsCar fontSize="small" />, // Added vehicle icon
        feature: "vehicle_task_management",
      },
      {
        title: "Vehicle Lease Agreements",
        path: "/dashboard/vehicle-lease-agreements",
        icon: <CarRental fontSize="small" />,
        feature: "vehicle_task_management",
      },
      {
        title: "Tasks",
        path: "/dashboard/tasks",
        icon: <AssignmentIcon fontSize="small" />, // Changed to assignment/task icon
        feature: "vehicle_task_management",
      },
      {
        title: "Task Templates",
        path: "/dashboard/task-templates",
        icon: <FileCopyIcon fontSize="small" />, // Changed to template/copy icon
        feature: "vehicle_task_management",
      },
    ],
  },

  {
    title: "Fleet & Assets",
    items: [
      {
        title: "Site Board",
        path: "/dashboard/asset-board",
        icon: <GridOn fontSize="small" />,
        feature: "web_site_board",
      },

      {
        title: "Live Condition Board",
        path: "/dashboard/compartment-monitoring",
        icon: <GridOn fontSize="small" />,
        feature: "web_live_conditions",
      },

      {
        title: "Trips",
        path: "/dashboard/trips",
        icon: <Share fontSize="small" />,
        feature: "web_trips",
      },
      {
        title: "Trip Assignment",
        path: "/dashboard/trip-assignment",
        icon: <AssignmentIcon fontSize="small" />,
        feature: "logbook",
      },
      {
        title: "Inputs",
        path: "/dashboard/inputs",
        icon: <SettingsInputComponent fontSize="small" />,
        feature: "inputs",
      },
      {
        title: "Timeline",
        path: "/dashboard/timeline",
        icon: <SettingsInputComponent fontSize="small" />,
        feature: "web_timeline",
      },
      {
        title: "Logbook",
        path: "/dashboard/logbook",
        icon: <MenuBook fontSize="small" />,
        feature: "logbook",
      },
      {
        title: "Driver Safety",
        path: "/dashboard/driversafety",
        icon: <Warning fontSize="small" />,
        feature: "driver_safety",
      },

      {
        title: "Overview Map",
        path: "/dashboard/overview-map",
        icon: <AccountTree fontSize="small" />,
        feature: "web_overview_map",
      },
      {
        title: "Beacon Overview Map",
        path: "/dashboard/beacon-overview-map",
        icon: <AccountTree fontSize="small" />,
        feature: "web_beacon_overview_map",
      },
    ],
  },

  {
    title: "People & Operations",
    items: [
      {
        title: "Driver Management",
        path: "/dashboard/drivermanagement",
        icon: <People fontSize="small" />,
        feature: "driver_management",
      },
      {
        title: "Timesheet",
        path: "/dashboard/timesheet",
        icon: <Timer fontSize="small" />,
        feature: "timesheet",
        children: [
          {
            title: "View Timesheet",
            path: "/dashboard/timesheet/view",
            permissions: "review_timesheet",
          },
          {
            title: "Payroll",
            path: "/dashboard/timesheet/payroll",
            permissions: "admin_timesheet",
          },
          {
            title: "Review Timesheet",
            path: "/dashboard/timesheet/review",
            permissions: "access_timesheet",
          },
          {
            title: "Reports",
            path: "/dashboard/timesheet/reports",
            icon: <Warning fontSize="small" />,
            permission: "access_timesheet",
            children: [
              {
                title: "Job Report",
                path: "/dashboard/timesheet/reports/job",
                permission: "access_timesheet",
              },
              {
                title: "Structure Timesheet",
                path: "/dashboard/timesheet/reports/structure",
                permission: "access_timesheet",
              },
              {
                title: "Overtime Timesheet",
                path: "/dashboard/timesheet/reports/overtime",
                permission: "access_timesheet",
              },
              {
                title: "Missing Shifts",
                path: "/dashboard/timesheet/reports/missingShifts",
                permission: "access_timesheet",
              },
              {
                title: "No Login",
                path: "/dashboard/timesheet/reports/noLogin",
                permission: "access_timesheet",
              },
            ],
          },
        ],
      },
      {
        title: "Leave",
        path: "/dashboard/timesheet/leave-requests",
        icon: <CalendarMonth fontSize="small" />,
        feature: "timesheet",
        children: [
          {
            title: "All Leave Requests",
            path: "/dashboard/timesheet/all-leave-requests?admin=true",
            icon: <CalendarMonth fontSize="small" />,
          },
          {
            title: "Leave Requests",
            path: "/dashboard/timesheet/leave-requests",
            icon: <CalendarMonth fontSize="small" />,
          },
          {
            title: "Leave Calendar",
            path: "/dashboard/timesheet/leave-calendar",
            icon: <CalendarMonth fontSize="small" />,
          },
        ],
      },
      {
        title: "Tickets",
        path: "/dashboard/tickets",
        icon: <CardMembership fontSize="small" />,
        feature: "timesheet",
        children: [
          {
            title: "Ticket Admin",
            path: "/dashboard/tickets/admin",
            permissions: "tickets_edit",
          },
          {
            title: "Matrix",
            path: "/dashboard/tickets/matrix",
            permissions: "tickets_view",
          },
          {
            title: "Approvals",
            path: "/dashboard/tickets/approvals",
            permissions: "tickets_add",
          },
        ],
      },
      {
        title: "Forms",
        icon: <ListAlt fontSize="small" />,
        feature: "forms",
        children: [
          {
            title: "Admin",
            path: "/dashboard/forms/admin",
            permission: "forms_add",
          },
          {
            title: "Required Forms",
            path: "/dashboard/required-forms",
            permission: "forms_view",
          },
          {
            title: "Requested Forms",
            path: "/dashboard/forms/requested",
            permission: "forms_view",
          },
          {
            title: "Pinned Forms",
            path: "/dashboard/forms/pinned",
            permission: "forms_add",
          },
          {
            title: "Complete",
            path: "/dashboard/forms/selection",
            permission: "complete_forms",
          },
          {
            title: "View Completed",
            path: "/dashboard/forms/completed",
            permission: "forms_view",
          },
          {
            title: "Completed Prestarts",
            path: "/dashboard/forms/completed-prestarts",
            permission: "forms_view",
          },
          {
            title: "Review Forms",
            path: "/dashboard/forms/review",
            permission: "forms_edit",
          },
          {
            title: "Asset Dashboard Forms",
            path: "/dashboard/dashboard-forms",
            permission: "forms_add",
          },
        ],
      },
      {
        title: "Geofences / Sites",
        path: "/dashboard/geofences",
        icon: <BorderClearIcon fontSize="small" />,
        feature: "web_sites",
      },
    ],
  },
  {
    title: "Admin",
    items: [
      {
        title: "Company",
        path: "/dashboard/company_settings",
        icon: <Business fontSize="small" />,
        permission: "edit_permissions",
      },
      {
        title: "People",
        path: "/dashboard/admin/people",
        icon: <Person fontSize="small" />,
        permission: "people_view",
      },
      {
        title: "Beacons",
        path: "/dashboard/beacons",
        icon: <DeviceHub fontSize="small" />,
        feature: "ble_beacons",
      },
      {
        title: "Beacon Types",
        path: "/dashboard/beacon-config",
        icon: <SettingsInputAntenna fontSize="small" />,
        feature: "ble_beacons",
      },
      {
        title: "Driver Tags",
        path: "/dashboard/driver-tags",
        icon: <BadgeOutlined fontSize="small" />,
        feature: "driver_management",
      },
      {
        title: "Drivers & Trucks",
        path: "/dashboard/admin/truck-driver-sessions",
        icon: <LocalShipping fontSize="small" />,
        permission: "admin_timesheet",
      },
      {
        title: "Jobsites",
        path: "/dashboard/admin/jobsites",
        icon: <LocationOn fontSize="small" />,
        permission: "jobsites_view",
      },
      {
        title: "Site Directory",
        path: "/dashboard/site-directory",
        icon: <CalendarMonth fontSize="small" />,
        permission: "jobsites_view",
      },
      {
        title: "Assignment Settings",
        path: "/dashboard/vehicle-assignment/settings",
        icon: <SettingsInputComponent fontSize="small" />,
        permission: "jobsites_view",
      },
      {
        title: "Route Planning",
        path: "/dashboard/vehicle-assignment/routes",
        icon: <AccountTree fontSize="small" />,
        permission: "jobsites_view",
      },
      {
        title: "Import TCS Data",
        path: "/dashboard/vehicle-assignment/imports",
        icon: <UploadFile fontSize="small" />,
        permission: "jobsites_view",
      },
      {
        title: "Completed Routes",
        path: "/dashboard/vehicle-assignment/history",
        icon: <History fontSize="small" />,
        permission: "jobsites_view",
      },
      {
        title: "Route Issues",
        path: "/dashboard/vehicle-assignment/deviations",
        icon: <Warning fontSize="small" />,
        permission: "jobsites_view",
      },
      {
        title: "Groups",
        path: "/dashboard/admin/groups",
        icon: <Group fontSize="small" />,
        permission: "groups_view",
      },
      {
        title: "Jobcodes",
        path: "/dashboard/admin/jobcodes",
        icon: <Code fontSize="small" />,
        permission: "codes_view",
      },
      {
        title: "Account / Device Linking",
        path: "/dashboard/admin/accdevlink",
        icon: <Link fontSize="small" />,
      },
      {
        title: "Allowances",
        path: "/dashboard/admin/allowances",
        icon: <AccountBalance fontSize="small" />,
        permission: "admin_timesheet",
        feature: "timesheet",
      },
      {
        title: "Payroll Groups",
        path: "/dashboard/admin/payrollgroups",
        icon: <Payments fontSize="small" />,
        permission: "admin_timesheet",
        feature: "timesheet",
      },
    ],
  },
  {
    title: "Moncrete",
    feature: "concrete_curing",
    items: [
      {
        title: "Moncrete",
        path: "/dashboard/concrete/dashboard",
        icon: <Timer fontSize="small" />,
        children: [
          {
            title: "Dashboard",
            path: "/dashboard/concrete/dashboard",
          },
          {
            title: "Admin",
            path: "/dashboard/concrete",
            icon: <Warning fontSize="small" />,
            children: [
              { title: "Mix Design", path: "/dashboard/concrete/mix-designs" },
              {
                title: "Mass Concrete Limits",
                path: "/dashboard/concrete/mass-concrete-limits",
              },
              {
                title: "Evaporation Limits",
                path: "/dashboard/concrete/evaporation-limits",
              },
              {
                title: "Maturity Inputs",
                path: "/dashboard/concrete/maturity-inputs",
              },
              { title: "Pours", path: "/dashboard/concrete/Pours" },
              { title: "Sensors", path: "/dashboard/concrete/sensors" },
            ],
          },
        ],
      },
    ],
  },

  {
    title: "My Account",
    items: [
      {
        title: "My Account",
        path: "/dashboard/account",
        icon: <UserIcon fontSize="small" />,
      },
      {
        title: "Subscriptions",
        path: "/dashboard/subscriptions",
        icon: <LocalOffer fontSize="small" />,
      },
    ],
  },
];

const DashboardSidebar = (props) => {
  const { onMobileClose, openMobile } = props;
  const { settings, saveSettings } = useSettings();
  const [expanded, setExpanded] = useState(
    settings.navSidebarExpanded !== false
  );
  const [searchQuery, setSearchQuery] = useState("");
  const location = useLocation();
  const { user } = useAuth();
  const helpBrand = resolveHelpBrand(user);
  const pageGuide = contextualArticle(user, location.pathname);
  const accountAvatarSrc = user?.photo
    ? user.photo
    : user?.avatar
    ? `data:image/png;base64,${user.avatar}`
    : undefined;

  // Apply company access before menu search in both desktop and mobile views.
  const filterMenuItems = (query) => {
    const isTsAdmin = Boolean(user?.tsadmin);
    const hasVehicleAssignmentAccess = canAccessVehicleAssignment(
      user?.companyid
    );
    const isAllowedTsAdminItem = (item) =>
      item.path === "/dashboard/assets" ||
      item.title === "Fleet & Asset List" ||
      item.title === "Fleet & Asset Map";

    const lowercaseQuery = query.toLowerCase();

    return sections
      .map((section) => ({
        ...section,
        items: section.items.filter(
          (item) =>
            (!isVehicleAssignmentPath(item.path) || hasVehicleAssignmentAccess) &&
            (!isTsAdmin || isAllowedTsAdminItem(item)) &&
            (item.title.toLowerCase().includes(lowercaseQuery) ||
              (item.children &&
                item.children.some((child) =>
                  child.title.toLowerCase().includes(lowercaseQuery)
                )))
        ),
      }))
      .filter((section) => section.items.length > 0);
  };

  const filteredSections = filterMenuItems(searchQuery);
  const { features_enabled = {} } = user;
  const persistExpanded = (nextExpanded) => {
    setExpanded(nextExpanded);
    saveSettings({
      ...settings,
      navSidebarExpanded: nextExpanded,
    });
  };

  // Restore the sidebar size when navigating between pages.
  useEffect(() => {
    if (openMobile && onMobileClose) {
      onMobileClose();
    }

    const shouldAutoMinimise = AUTO_MINIMISE_URL.some((pattern) => {
      let regex = new RegExp(pattern);
      return regex.test(location.pathname);
    });

    if (shouldAutoMinimise) {
      setExpanded(false);
    } else {
      setExpanded(settings.navSidebarExpanded !== false);
    }
  }, [location.pathname, settings.navSidebarExpanded]);

  // 5. Add the handleSearch function
  const handleSearch = (event) => {
    setSearchQuery(event.target.value);
  };

  // 6. Add the clearSearch function
  const clearSearch = () => {
    setSearchQuery("");
  };

  const generateContent = (mobileMode) => (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
      }}
    >
      <Hidden lgUp>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            p: 2,
          }}
        >
          <RouterLink to="/">{/*<Logo color="black" />*/}</RouterLink>
        </Box>
      </Hidden>
      <Box sx={{ p: 2, position: "relative" }}>
        {expanded || mobileMode ? (
          <Box
            sx={{
              mt: { xs: 4, lg: 0 },
              alignItems: "center",
              backgroundColor: "background.default",
              borderRadius: 1,
              display: "flex",
              overflow: "hidden",
              p: 2,
            }}
          >
            <RouterLink to="/dashboard/account">
              <Avatar
                src={accountAvatarSrc}
                sx={{
                  cursor: "pointer",
                  height: 48,
                  width: 48,
                }}
              />
            </RouterLink>
            <Box sx={{ ml: 2 }}>
              <Typography color="textPrimary" variant="subtitle2">
                {user.name}
              </Typography>
              <Typography color="textSecondary" variant="body2">
                <Link color="primary" component={RouterLink} to="/dashboard">
                  {user.first_name} {user.last_name}
                </Link>
              </Typography>
            </Box>
          </Box>
        ) : (
          <Box
            sx={{
              py: 2,
            }}
          >
            <RouterLink to="/dashboard/account">
              <Avatar
                src={accountAvatarSrc}
                sx={{
                  cursor: "pointer",
                  height: 48,
                  width: 48,
                }}
              />
            </RouterLink>
          </Box>
        )}
        <IconButton
          onClick={() => {
            persistExpanded(!expanded);
          }}
          sx={{
            display: { xs: "none", sm: "none", md: "none", lg: "flex" },
            position: "absolute",
            right: 0,
            bottom: 0,
            transform: "translate(50%, 50%)",
            zIndex: 1000,
            backgroundColor: colors.white,
            border: "1px solid rgba(0, 0, 0, 0.12)",
            boxShadow: "0 8px 20px rgba(15, 23, 42, 0.14)",
            "&:hover": {
              backgroundColor: colors.white,
              boxShadow: "0 10px 24px rgba(15, 23, 42, 0.2)",
            },
          }}
          color="primary"
        >
          {!expanded ? (
            <ChevronRightIcon fontSize="small" />
          ) : (
            <ChevronLeftIcon fontSize="small" />
          )}
        </IconButton>
      </Box>
      <Divider />

      <Box
        sx={{
          alignItems: expanded || mobileMode ? "stretch" : "center",
          display: "flex",
          px: 2,
          pt: expanded || mobileMode ? 2 : 1.5,
          pb: expanded || mobileMode ? 2 : 1.5,
          justifyContent: expanded || mobileMode ? "center" : "flex-start",
        }}
      >
        {expanded || mobileMode ? (
          <TextField
            fullWidth
            placeholder="Search menu..."
            value={searchQuery}
            onChange={handleSearch}
            variant="outlined"
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: searchQuery && (
                <InputAdornment position="end">
                  <IconButton edge="end" onClick={clearSearch} size="small">
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        ) : (
          <Tooltip title="Search menu" placement="right">
            <IconButton
              onClick={() => {
                persistExpanded(true);
              }}
              sx={{
                color: "text.secondary",
                height: 48,
                justifyContent: "center",
                padding: 0,
                width: 48,
                backgroundColor: "transparent",
                "& svg": {
                  opacity: 0.65,
                },
                "&:hover": {
                  backgroundColor: "transparent",
                  color: "text.primary",
                },
              }}
            >
              <SearchIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>
      <Scrollbar options={{ suppressScrollX: true }}>
        <Box sx={{ p: 2 }}>
          {filteredSections.map((section) => {
            if (
              (section.permission && !user?.permissions[section.permission]) ||
              !isCompanyFeatureEnabled(features_enabled, section.feature)
            ) {
              return null;
            }

            return (
              <NavSection
                user={user}
                setExpanded={persistExpanded}
                expanded={expanded || mobileMode}
                key={section.title}
                pathname={location.pathname}
                sx={{
                  "& + &": {
                    mt: 3,
                  },
                }}
                {...section}
              />
            );
          })}
        </Box>
        <Divider />
      </Scrollbar>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          flexShrink: 0,
          borderTop: "1px solid",
          borderColor: "divider",
          p: expanded || mobileMode ? 1.5 : 1,
        }}
      >
        <Tooltip title={expanded || mobileMode ? "" : "Help Centre"} placement="right">
          <Button
            component={RouterLink}
            to="/docs"
            fullWidth
            startIcon={expanded || mobileMode ? <HelpOutline /> : undefined}
            aria-label="Help Centre"
            sx={{ justifyContent: expanded || mobileMode ? "flex-start" : "center" }}
          >
            {expanded || mobileMode ? "Help Centre" : <HelpOutline />}
          </Button>
        </Tooltip>
        {(expanded || mobileMode) && pageGuide && (
          <Button
            component={RouterLink}
            to={articlePath(pageGuide)}
            fullWidth
            startIcon={<MenuBook />}
            sx={{ justifyContent: "flex-start" }}
          >
            Guide for this page
          </Button>
        )}
        {helpBrand.supportUrl && (
          <Tooltip title={expanded || mobileMode ? "" : "Contact support"} placement="right">
            <Button
              component="a"
              href={helpBrand.supportUrl}
              target="_blank"
              rel="noopener noreferrer"
              fullWidth
              startIcon={expanded || mobileMode ? <OpenInNew /> : undefined}
              aria-label="Contact support"
              sx={{ justifyContent: expanded || mobileMode ? "flex-start" : "center" }}
            >
              {expanded || mobileMode ? "Contact support" : <OpenInNew />}
            </Button>
          </Tooltip>
        )}
      </Box>
    </Box>
  );

  return (
    <>
      <Hidden lgUp>
        <Drawer
          anchor="left"
          onClose={onMobileClose}
          open={openMobile}
          PaperProps={{
            sx: {
              backgroundColor: "background.paper",
              width: DRAWER_WIDTH,
            },
          }}
          variant="temporary"
        >
          {generateContent(true)}
        </Drawer>
      </Hidden>
      <Hidden lgDown>
        <ExpandableDrawer
          style={{ overflow: "visible", zIndex: 1222 }}
          anchor="left"
          open={expanded}
          onClose={() => {
            persistExpanded(true);
          }}
          PaperProps={{
            sx: {
              backgroundColor: "background.paper",
              height: "calc(100% - 64px) !important",
              top: "64px !Important",
              overflow: "visible !important",
              width: DRAWER_WIDTH,
            },
          }}
          variant="permanent"
        >
          {generateContent()}
        </ExpandableDrawer>
      </Hidden>
    </>
  );
};

DashboardSidebar.propTypes = {
  onMobileClose: PropTypes.func,
  openMobile: PropTypes.bool,
};

export default DashboardSidebar;
